import { EventEmitter } from '@angular/core';
import { UiValidator } from '../common/field-validation/ui-validator.dto';
import { QueryOperand } from '../common/kibana-request-dto';
import { CohortDO } from '../common/domain-model-dtos';
import { CurrencyDTO } from '../components/data-type-widgets/data-type/lottomart/model/lottomart-model';
import { DataType } from '../components/data-type-widgets/data-type/data-type-component';

export interface LookupChange {
  field: string;
  value: any;
}

export enum MenuType {
  MULTIPLE = 'MULTIPLE',
  MAINTAIN = 'MAINTAIN',
  ENTITY_GROUP_SINGLE_FORM = 'ENTITY_GROUP_SINGLE_FORM',
  ENTITY_GROUP = 'ENTITY_GROUP',
  ELASTIC = 'ELASTIC',
  CASSANDRA = 'CASSANDRA',
  DASHBOARD = 'DASHBOARD',
  TICKER = 'TICKER',
}

export interface ChartDTO {
  label: string;
  menuItems: MenuDTO[];
}

export interface ChartOptionsDO {
  animation: boolean;
  scaleOverride: boolean;
  scaleSteps: number;
  scaleStepWidth: number;
  scaleStartValue: number;
}

export interface ChartDatasetDO {
  datasetIndex: number;
  id: number;
  chartKey: string;
  key: string;
  label: string;
  backgroundColor: string[] | string;
  borderColor: string[] | string;
  borderWidth: number[] | number;
  hoverBackgroundColor: string[];
  hoverBorderColor: string[];
  hoverBorderWidth: number[];
}

export interface ChartCategoryDO {
  categoryKey: string;
  label: string;
  rank: number;
}

export interface PieChartOptionsDO {
  cutoutPercentage: number;
  legend: any;
}

export interface ChartDO {
  id: number;
  key: string;
  title: string;
  type: string;
  data: any;
  datasets: any;
  labels: any;
  chartDatasets: ChartDatasetDO[];
  chartCategories: ChartCategoryDO[];
  options: any;
  pieOptions: PieChartOptionsDO;
  width: number;
  height: number;
  chart: any; // the actual chart
}

export interface ChartDatasetDataDO {
  id: number;
  datasetId: number;
  rank: number;
  value: number;
}

export interface ToolbarItemDTO {
  routerLink: string;
  tooltip: string;
  icon: string;
  leftSeparator: boolean;
  displayOrder: number;
}

export interface ToolbarTickerDTO {
  code: string;
  icon: string;
  displayOrder: number;
  leftSeparator: boolean;
}

export interface LookupEntityConf {
  name: string;
  // extractLabel: (any) => string;
  extractLabel: string;
  tableFields: string[];
  sortBy: string;
  pageable: boolean;
  // retValue?: (any) => any;
  retValue?: string;
  filterRetValue?: (any) => any;
  fieldTypes?: string[];
  fieldLabels?: string[];
  externalUrl?: string; // {data} is content of lookup, {label} is label
  uniqueId?: string;
  extractImage?: string;
  showImage?: boolean;
  defaultSelectUrl?: string;
  // uniqueId?: ((a1: any) => any);
}

export interface SystemDTO {
  menuGroups: MenuGroupDTO[];
  forms: FormDTO[];
  charts: { [key: string]: ChartDO };
  cohorts: CohortDO[];
  toolbarItems: ToolbarItemDTO[];
  toolbarTickers: ToolbarTickerDTO[];
  lookups: LookupEntityConf[];
  pageSizes: number[];
  // charts: { [key: string]: Chart };
}

export interface MenuGroupDTO {
  label: string;
  menuItems: MenuDTO[];
  icon: string;
  alwaysOpen: boolean;
}

export interface FormDTO {
  code: string;
  title: string;
  updateUrl: string;
  rowConfig: FormRowConfigDTO;
}

export interface MenuDTO {
  label: string;
  code: string;
  help?: string;
  selectUrl?: string;
  menuType?: MenuType;
  entityGroup?: EntityGroupConfigDTO;
  singleForm?: SingleFormDTO;
  columnBasedLayout?: boolean;
  withTimestamp?: boolean;
  refreshRate?: number;
  showInSideBar?: boolean;
  active?: boolean;
  tabGroup?: string;
}

export interface SelectConditionDO {
  id: number;
  name: string;
  formConfig: FormRowConfigDTO[];
  displayOrder: number;
  dataTemplate: any;
}

export interface SelectDeeplinkDO {
  id: number;
  name: string;
  formConfig: FormRowConfigDTO[];
  displayOrder: number;
  dataTemplate: any;
}

export interface SelectBundleDO {
  id: number;
  name: string;
  formConfig: FormRowConfigDTO[];
  displayOrder: number;
  dataTemplate: any;
}

export interface SelectRuleDO {
  id: number;
  name: string;
  formConfig: FormRowConfigDTO[];
  displayOrder: number;
  dataTemplate: any;
}

export interface SelectOfferDO {
  id: number;
  name: string;
  formConfig: FormRowConfigDTO[];
  displayOrder: number;
  dataTemplate: any;
}

export interface SelectSmartFilterDO {
  id: number;
  name: string;
  formConfig: FormRowConfigDTO[];
  displayOrder: number;
  dataTemplate: any;
  description: string;
}

export interface MaintainTabDTO {
  endpoints?: EndpointsConfigDTO;
  tableView: TableViewConfigDTO;
  initialConditions?: RowConditionDTO[];
}

export interface MaintainConfigDTO {
  id?: string;
  tabs: MaintainTabDTO[];
  rows: FormRowConfigDTO[];
  restActions?: ActionConfigDTO[];
  overallRestActions?: ActionConfigDTO[];
  tabsWithCount: boolean;
  countUrl: string;
  title: string;
  editUrl?: string;
}

export interface TickerConfigDTO extends MaintainConfigDTO {
  commands: TickerCommandDTO[];
  code: string;
  locks: boolean;
  multiSelect: boolean;
  elasticFilterGroup: string;
}

export interface ElasticConfigDTO extends MaintainConfigDTO {
  withTimestamp: boolean;
  convertTo: string;
  indexClass: string;
  indexName: string;
  elasticFilterGroup: string;
  includeCohortCondition: boolean;
}

export interface CassandraConfigDTO extends MaintainConfigDTO {
  convertTo: string;
  selectUrl: string;
}

export interface DashboardConfigDTO {
  title: string;
  columns: FormColumnConfigDTO[];
}

export class EndpointsConfigDTO {
  selectAllUrl: string;

  selectOneUrl?: string;

  insertUrl?: string;

  updateUrl?: string;

  deleteUrl?: string;
}

export interface FilterConfigDTO {
  name: string;
  validators: UiValidator[];
  value: string;
}

export interface RowConditionDTO {
  field: string;
  value: string;
  operand: QueryOperand;
}

export interface ConditionalRowDetailsDTO {
  conditions: RowConditionDTO[];
  url: string;
  detailsConfig: FormRowConfigDTO;
  expandable: boolean;
  grouped: boolean;
  detailsButton: boolean;
}

class SortDTO {
  field: string;

  direction: string;
}

export interface TableViewConfigDTO {
  title: string;
  add: AddConfigDTO;
  actions: TableActionsConfigDTO[];
  rowsPerPageOptions: number[];
  code: string;
  filters: FieldConfigDTO[];
  conditionals?: ConditionalRowDetailsDTO[];
  defaultSort: SortDTO;
  aggsUrl: string;
  sortTieBreaker: string;
  paged: boolean;
  expandable: boolean;
  filterColumns: boolean;
}

export interface AddConfigDTO {
  allowAdd: boolean;
  title?: string;
}

export interface TableActionsConfigDTO {
  tag: string;
  pageTitle?: string;
  overrideHandle?: boolean;
  cssIcon?: string;
  tooltip?: string;
  conditional?: string;
}

export interface ActionConfigBaseDTO {
  buttonLabel: string;
  popUpTitle: string;
  popUpDescription: string;
  jsEditCondition: string;
  successMessage: string;
  rows: FormRowConfigDTO[];
  confirmationFormRows: FormRowConfigDTO[];
  minModalWidth: string;
  minModalHeight: string;
  parentMenuLabel: string;
  jsWarningCondition: string;
  warningMessage: string;
  confirmationMessage: string;
  confirmationUrl: string;
  confirmationFields: string[];
  downloadAction: boolean;
  multipartForm: boolean;
  dynamicResponse: boolean;
  displaySummary: boolean;
  customModal?: string;
}

export interface ActionConfigDTO extends ActionConfigBaseDTO {
  method: string;
  url: string;
  children: ActionConfigDTO[];
  ref: RefDTO;
}

export interface TickerCommandDTO extends ActionConfigBaseDTO {
  command: string;
  confirmationRequired?: boolean;
  icon?: string;
  quickAccess?: boolean;
  actionsBar?: boolean;
}

export interface DynamicResponseDTO {
  message: string;
  copyableMessage: string;
  type: string;
}

export class FormColumnConfigDTO {
  fields: FieldConfigDTO[];

  code?: string;

  title?: string;

  css?: string;

  ignorePanel?: boolean;

  updateUrl?: string;

  fullWidth: boolean;

  nested?: boolean;

  jsEditCondition?: string;

  actions?: ActionConfigDTO[];

  fxFlex: string;

  multipartForm?: boolean;

  hasTitle?: boolean;

  columns?: number;

  requireUpdateReason?: boolean;
}

export interface FormRowConfigDTO {
  columns: FormColumnConfigDTO[];
  css?: string;
}

export interface SingleFormDTO {
  rows: FormRowConfigDTO[];
  title: string;
  updateUrl: EndpointDTO;
  jsEditCondition: string;
  multipartForm: boolean;
}

export class EndpointDTO {
  url: string;

  method: string;

  fields: string[];
}

export interface RefDTO {
  nestedId?: any;
  entity?: string;
  code?: string;
  tab?: string;
}

export interface MetricOptionsDTO {
  name: string;
}

class TreeOptionsDTO {
  types: { [key: string]: TreeTypeDefDTO };

  depth: number;

  formFixedPositioned: boolean;
}

export interface TreeTypeDefDTO {
  type: string;
  updateUrl: string;
  addUrl: string;
  reorderUrl: string;
  innerFields?: FieldConfigDTO[];
  deleteUrl?: string;
  refreshViewAfterUpdate?: boolean;
}

export interface DurationPickerOptions {
  showNegative: boolean;
  showButtons: boolean;
  showPreview: boolean;
  showLetters: boolean;
  showYears: boolean;
  showMonths: boolean;
  showWeeks: boolean;
  showDays: boolean;
  showHours: boolean;
  showMinutes: boolean;
  showSeconds: boolean;
  zeroValue: string | null;
}

export interface FieldConfigDTO {
  columnNotFilterable?: boolean;
  sticky?: boolean;
  name: string;
  label: string;
  type: DataType;
  filterApplyFor?: string[];
  externalLink?: string;
  reorderWeightField?: string;
  filterElasticName?: string;
  elasticSearchOperand?: QueryOperand;
  sortableField?: string;
  sortable?: boolean;
  canAdd?: string;
  canRemove?: string;
  innerFields?: FieldConfigDTO[];
  formFullWidth?: boolean;
  displayInTable?: boolean;
  filterInTable?: boolean;
  advancedFilter?: boolean;
  addIgnore?: boolean;
  editHide?: boolean;
  editReadOnly?: string;
  addReadOnly?: boolean;
  placeholder?: string;
  validators?: UiValidator[];
  cssClassPrefix?: string;
  options?: string[];
  child?: any;
  noLabel?: boolean;
  lookup?: string;
  moneyFormat?: string;
  category?: string;
  labelCss?: string;
  entryCss?: string;
  tableCss?: string;
  lookupArg?: string;
  propertyName?: string;
  dateFormat?: string;
  selected?: EventEmitter<any>;
  drawOptions?: any[];
  decimalPlaces?: number;
  lookupData?: any[];
  // lookupConfig?: TableField[];
  lookupFilterable?: boolean;
  nestedTable?: FieldConfigDTO[];
  fixedCurrency?: boolean;
  suffix?: string;
  timezone?: string;
  rowConfig?: FormRowConfigDTO;
  selectUrl?: string;
  value?: any;
  alignRight?: boolean;
  idFieldName?: string;
  ignoreIfNotPresent?: boolean;
  ref?: RefDTO;
  standaloneTableConfig?: MenuDTO;
  localeFormatted?: boolean;
  align?: string;
  jsValue?: string;
  jsRecalculate?: string;
  viewHide?: boolean;
  lookupLayout?: string;
  canReorder?: boolean;
  codeMode?: string;
  theme?: string;
  aggs?: AggFieldConfigDO[];
  lookupInsertUrl?: string;
  metricOptions?: MetricOptionsDTO;
  treeOptions?: TreeOptionsDTO;
  newTableElementUrl?: string;
  strippedRows?: boolean;
  expandablePanel?: string;
  hideIfEmpty?: boolean;
  editIfTicked?: boolean;
  clipboard?: boolean;
  actions?: ActionConfigDTO[];
  durationOptions?: DurationPickerOptions;
  properties?: any;
  panelOptionsUrl?: any;
  lookupWatcherFieldName?: string;
  lookupWatcherFieldObjectId?: string;
  help?: string;
  objectKey?: boolean;
  clearableImgLookup?: boolean;
  requiresKicktheDirectory?: boolean;
  sessionUserEmailFilter?: boolean;
  presentStopwatchSeconds?: boolean;
  tableWidth?: string;
  ellipsisMaxWidth?: number;
}

export enum AggFieldType {
  SUM = 'SUM',
  AVG = 'AVG',
}

export interface AggFieldConfigDO {
  type: AggFieldType;
}

export interface MoneyDTO {
  currency: CurrencyDTO;
  value: number;
}

export interface DataSourceDefDTO {
  type: MenuType;
  definition: MaintainConfigDTO;
}

export interface EntityGroupConfigDTO {
  titleTag: string;
  menuItems: MenuDTO[];
  title: string;
  heko: string;
  selectUrl: string;
  updateUrl?: string;
  header: EntityGroupHeaderDTO;
  createConfig: MenuDTO;
  entryForm: MenuDTO;
  selectTableType: MenuType;
  dataSources: DataSourceDefDTO[];
  currentMode?: number;
}

export interface EntityGroupHeaderDTO {
  rows: FormRowConfigDTO[];
  backgroundColor: string;
  url: string;
  fontColor: string;
  title: string;
  image: string;
  status: string;
  actions: ActionConfigDTO[];
}

export interface DateRangeDTO {
  from: Date;
  to: Date;
}

export interface MapDTO {
  lat: number;
  lng: number;
  zoom: number;
  zoomControl: boolean;
  markers: LocationMarkerDTO[];
  circle: LocationCircleDTO;
}

export interface LocationMarkerDTO {
  lat: number;
  lng: number;
  label?: string;
  draggable?: boolean;
}

export interface LocationCircleDTO {
  lat: number;
  lng: number;
  radius: number;
  fillColor: string;
  circleDraggable: boolean;
  editable: boolean;
}

export interface AdminGaugeDO {
  name: string;
  value: number;
}

export interface LinkType {
  label: string;
  code: string;
  menuItem?: MenuDTO;
}
